import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactPaginate from "react-paginate";
import BaseUrl from "../../../constants/BaseUrl";
import ParagraphTag from "../../reusebale/Paragraph";
import MyLoader from "../../reusebale/myLoader";
import { DollarSign, Calendar, FileText, CheckCircle, Clock, XCircle, Hourglass } from "lucide-react";

const TransactionHistory = ({ shouldRefresh }) => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 0,
    limit: 10,
  });

  const itemsPerPage = 10;

  // Fetch transaction history
  const fetchTransactions = async (page = 1) => {
    if (!loginSuccess?.user?._id || !loginSuccess?.token) return;

    try {
      setLoading(true);
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

      const response = await fetch(
        `${BaseUrl}api/payment-transaction/tenant/${loginSuccess.user._id}?page=${page}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        }
      );
      const result = await response.json();

      if (result.status === "success" && result.data) {
        setTransactions(result.data);
        if (result.pagination) {
          setPagination(result.pagination);
        }
      } else {
        setTransactions([]);
        setPagination({
          total: 0,
          currentPage: 1,
          totalPages: 0,
          limit: itemsPerPage,
        });
      }
    } catch (error) {
      console.error("Error fetching transaction history:", error);
      setTransactions([]);
      setPagination({
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: itemsPerPage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(1);
  }, [loginSuccess, shouldRefresh]);

  const handlePageClick = (selectedPage) => {
    const newPage = selectedPage.selected + 1; // Convert 0-indexed to 1-indexed
    fetchTransactions(newPage);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      const timePart = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${datePart}, ${timePart}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase() || "";
    
    // Green statuses: COMPLETED, SUCCESS, COMPLETE, REFUND
    if (statusUpper === "COMPLETED" || statusUpper === "SUCCESS" || statusUpper === "COMPLETE" || statusUpper === "REFUND") {
      return (
        <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle size={12} />
          {status}
        </span>
      );
    } 
    // Yellow statuses: SCHEDULED, PENDING
    else if (statusUpper === "SCHEDULED" || statusUpper === "PENDING") {
      return (
        <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock size={12} />
          {status}
        </span>
      );
    } 
    // Blue status: NEW
    else if (statusUpper === "NEW") {
      return (
        <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-blue-100 text-blue-800 flex items-center gap-1">
          <DollarSign size={12} />
          {status}
        </span>
      );
    } 
    // Grey status: PROCESSING
    else if (statusUpper === "PROCESSING") {
      return (
        <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-gray-200 text-gray-700 flex items-center gap-1">
          <Hourglass size={12} />
          {status}
        </span>
      );
    } 
    // Red statuses: FAILED, REJECTED
    else if (statusUpper === "FAILED" || statusUpper === "REJECTED") {
      return (
        <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle size={12} />
          {status}
        </span>
      );
    }
    
    // Default: blue (same as NEW) for unknown statuses
    return (
      <span className="px-2 py-1 text-xs font-popinsSemiBold rounded bg-blue-100 text-blue-800">
        {status || "UNKNOWN"}
      </span>
    );
  };

  return (
    <div className="px-4 pb-4">
      <div className="border-t border-gray-200 pt-4 mt-2 relative">
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex justify-center items-center z-10 rounded-lg min-h-[200px]">
            <MyLoader />
          </div>
        )}

        {/* Content */}
        <div className={loading ? "pointer-events-none" : ""}>
          <ParagraphTag
            content="Transaction History"
            classes="text-darkColor font-popinsSemiBold text-base mb-3"
          />

        {transactions && transactions.length > 0 ? (
          <div className="space-y-3">
            {transactions.map((transaction, index) => (
              <div
                key={transaction._id || transaction.transactionId || index}
                className="relative bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <ParagraphTag
                        content={transaction.purpose || "Lease Payment"}
                        classes="text-darkColor font-popinsSemiBold text-sm"
                      />
                      <div className="flex items-center gap-1 mt-1">
                        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-gray-600 font-popinsRegular text-xs">
                          ID: {transaction.transactionId || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(transaction.status)}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                  <div>
                    <span className="text-gray-600 font-popinsMedium text-xs">Amount</span>
                    <div className="mt-1">
                      <ParagraphTag
                        content={formatCurrency(transaction.amount - (transaction.security_deposit || 0) - (transaction.security_deposit_refund || 0))}
                        classes="text-darkColor font-popinsSemiBold text-sm"
                      />
                      {(transaction.security_deposit_refund && transaction.security_deposit_refund > 0) ? (
                        <>
                          <ParagraphTag
                            content={`Sec. Deposit: ${formatCurrency(transaction.security_deposit_refund)}`}
                            classes="text-darkColor font-popinsSemiBold text-xs mt-0.5"
                          />
                          <ParagraphTag
                            content="REFUNDED"
                            classes="text-green-600 font-popinsSemiBold text-xs mt-0.5"
                          />
                        </>
                      ) : (transaction.security_deposit && transaction.security_deposit > 0) && (
                        <ParagraphTag
                          content={`Sec. Deposit: ${formatCurrency(transaction.security_deposit)}`}
                          classes="text-darkColor/60 font-popinsRegular text-xs mt-0.5"
                        />
                      )}
                      <ParagraphTag
                        content={`Total: ${formatCurrency(transaction.amount)}`}
                        classes="text-darkColor font-popinsSemiBold text-xs mt-0.5"
                      />
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600 font-popinsMedium text-xs">Date</span>
                    <div className="flex items-center gap-1 mt-1">
                      <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <ParagraphTag
                        content={formatDate(transaction.createdAt)}
                        classes="text-darkColor font-popinsRegular text-xs"
                      />
                    </div>
                  </div>
                </div>

                {(() => {
                  // For tenant: External → Internal = DEBIT (money leaving), Internal → External = CREDIT (money coming in)
                  const isDebit = transaction.sourceAccountType === "external" && transaction.destinationAccountType === "internal";
                  const isCredit = transaction.sourceAccountType === "internal" && transaction.destinationAccountType === "external";
                  const displayAmount = transaction.amount;
                  
                  if (isDebit || isCredit) {
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        {isDebit && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-popinsMedium text-xs">Debit (DR)</span>
                            <span className="text-red-600 font-popinsSemiBold text-sm">
                              {formatCurrency(displayAmount)}
                            </span>
                          </div>
                        )}
                        {isCredit && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-popinsMedium text-xs">Credit (CR)</span>
                            <span className="text-green-600 font-popinsSemiBold text-sm">
                              {formatCurrency(displayAmount)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
            <DollarSign className="w-12 h-12 text-gray-400 mb-2" />
            <ParagraphTag
              content="No transactions found"
              classes="text-darkColor/60 font-popinsMedium text-sm"
            />
            <ParagraphTag
              content="Your payment transactions will appear here"
              classes="text-darkColor/40 font-popinsRegular text-xs mt-1"
            />
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="mt-5">
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              pageCount={pagination.totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={handlePageClick}
              forcePage={pagination.currentPage - 1}
              containerClassName={"pagination flex justify-center items-center gap-2"}
              pageClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
              pageLinkClassName={"text-darkColor font-popinsMedium text-sm"}
              activeClassName={"bg-themeColor text-white border-themeColor"}
              previousClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
              nextClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
              disabledClassName={"opacity-50 cursor-not-allowed"}
            />
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;

